/**
 * Telephony context  (ADDITIVE — INBOUND CALLING, Phase IV)
 *
 * Context + types ONLY. Deliberately its own module, with no components in it,
 * so that `useTwilioVoiceDevice` can read the context and `TelephonyProvider`
 * can call the engine without the two importing each other in a cycle.
 * (The type imports below are `import type`, so they erase at runtime.)
 */

import { createContext, useContext } from 'react';
import type {
  UseTwilioVoiceDeviceResult,
  CallDirection,
} from '../hooks/useTwilioVoiceDevice';

/**
 * Everything we know about a call that is RINGING but not yet answered.
 *
 * All of it comes from the Twilio Call's own parameters (which Twilio populates
 * from the inbound webhook) or from a server response — never from arbitrary
 * browser state. A follower tab cannot fabricate one of these and make a modal
 * appear: the modal only renders for the leader, and only from a real
 * Device 'incoming' event.
 */
export interface IncomingCallInfo {
  /** Twilio's CallSid for the inbound leg. Undefined if the SDK didn't supply it. */
  providerCallSid?: string;
  /** The PSTN caller, E.164. */
  fromPhone: string;
  /** The workspace DID that was dialled. */
  toPhone: string;
  /** Display name, when the server could safely resolve one. */
  callerName?: string;
  contactId?: string;
  /**
   * The CRM's own contact id for the caller, when the server could resolve one.
   * Drives opening the Contact Workspace (Notes + Research) on answer — parity
   * with an outbound Contact Workspace call. Absent for unknown callers.
   */
  crmContactId?: string;
  /** The workspace this call belongs to — used to reject cross-workspace calls. */
  workspaceId: string;
  receivedAt: number;
  /** True when the server indicated the call is being recorded. */
  recording?: boolean;
}

/**
 * The incoming half of the call state machine.
 *
 * It is deliberately SEPARATE from `ActiveCallState` rather than adding new
 * variants to it. Adding `incoming_ringing` / `rejected` / `canceled` to the
 * existing union would force every existing exhaustive switch (ActiveCallShell,
 * ContactWorkspaceCallColumn, useCallTerminalRefetch) to grow branches — i.e.
 * it would change existing outgoing behaviour. Instead:
 *
 *   idle → incoming_ringing → accepting → (hand off to ActiveCallState.in_call)
 *                           ↘ rejected
 *                           ↘ canceled
 *
 * Once accepted, the call becomes an ordinary `in_call` with
 * `direction: 'inbound'`, and `ended` / `failed` are reached through the
 * existing machine. No existing switch ever sees a new `kind`.
 */
export type IncomingCallState =
  | { kind: 'idle' }
  | { kind: 'incoming_ringing'; info: IncomingCallInfo }
  | { kind: 'accepting'; info: IncomingCallInfo }
  | { kind: 'rejected'; info: IncomingCallInfo }
  | { kind: 'canceled'; info: IncomingCallInfo }
  | { kind: 'failed'; info: IncomingCallInfo; message: string };

export interface TelephonyContextValue {
  /**
   * The shared voice device. EXACTLY the pre-existing
   * `UseTwilioVoiceDeviceResult` shape — no raw Twilio Device or Call object is
   * reachable through it.
   */
  voice: UseTwilioVoiceDeviceResult;

  /** Ringing-call state. `idle` unless this tab is the leader and a call is in. */
  incoming: IncomingCallState;

  /** Accept the ringing call. No-op unless `incoming.kind === 'incoming_ringing'`. */
  acceptIncoming: () => Promise<void>;

  /** Reject the ringing call — a REAL Twilio reject, which lets the server's
   *  <Dial action> fall through to voicemail. Never a fake local completion. */
  rejectIncoming: () => void;

  /** Clear a terminal incoming state (rejected/canceled/failed) from the UI. */
  dismissIncoming: () => void;

  /** True when this tab holds the incoming-call lease for (crm, workspace, user). */
  isLeader: boolean;

  /** Server-authoritative: the token's VoiceGrant permits incoming calls. */
  incomingAllowed: boolean;

  /**
   * ADDITIVE (incoming per-call recording). The current user's toggle
   * choice for the CURRENTLY ringing Call SID. Defaults to `false` if
   * no toggle has been set or no call is ringing. Cleared automatically
   * when the invite terminates.
   */
  incomingRecordingPreference: boolean;

  /**
   * Flip the recording preference for the given Call SID. Broadcasts
   * to the same user's other surface via the existing realtime hub.
   * Non-blocking — the local state updates immediately regardless of
   * broadcast success, so accept-time behaviour always follows the
   * local device's latest choice.
   */
  setIncomingRecordingPreference: (callSid: string, record: boolean) => void;
}

export const TelephonyContext = createContext<TelephonyContextValue | null>(null);

/**
 * Read the shared voice device, or null when no provider is mounted.
 *
 * Used by `useTwilioVoiceDevice` to decide whether to suspend its local engine.
 * Returns null rather than throwing precisely so that surfaces rendered outside
 * the authenticated shell (and standalone unit tests) keep working exactly as
 * they did before this provider existed.
 */
export function useSharedTelephony(): UseTwilioVoiceDeviceResult | null {
  const ctx = useContext(TelephonyContext);
  return ctx ? ctx.voice : null;
}

/**
 * Full telephony context, including the incoming-call surface.
 * Throws outside a provider — callers of this one (the modal) are only ever
 * mounted inside the shell.
 */
export function useTelephony(): TelephonyContextValue {
  const ctx = useContext(TelephonyContext);
  if (!ctx) {
    throw new Error('useTelephony must be used within a <TelephonyProvider>');
  }
  return ctx;
}

/** Convenience: the direction of the current call, if any. */
export function directionOf(
  voice: UseTwilioVoiceDeviceResult
): CallDirection | null {
  return voice.activeCall.kind === 'idle'
    ? null
    : (voice.activeCall.direction ?? 'outbound');
}
